import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Save, X, Image as ImageIcon } from "lucide-react";
import MediaPicker from "./MediaPicker";

const CATEGORIES = ["Service", "Education", "Social", "Holiday", "Community"];

const emptyForm = {
  title: "",
  description: "",
  start_date: "",
  start_time: "",
  end_time: "",
  location: "",
  category: "Service",
  recurring: false,
  rsvp_required: false,
  flyer_url: "",
  rsvp_url: "",
  payment_url: "",
  details_url: "",
  featured: false,
};

export default function AdminEvents() {
  const { authedClient } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, "new" or event.id
  const [form, setForm] = useState(emptyForm);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/events");
      setEvents(data);
    } catch {
      toast.error("Could not load events");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setForm(emptyForm);
    setEditing("new");
  };

  const startEdit = (evt) => {
    setForm({ ...emptyForm, ...evt });
    setEditing(evt.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.start_date) {
      toast.error("Title, description, and start date are required");
      return;
    }
    try {
      if (editing === "new") {
        const { data } = await authedClient().post("/admin/events", form);
        setEvents((prev) =>
          [...prev, data].sort((a, b) => a.start_date.localeCompare(b.start_date))
        );
        toast.success("Event created");
      } else {
        const { data } = await authedClient().patch(`/admin/events/${editing}`, form);
        setEvents((prev) =>
          prev.map((e) => (e.id === editing ? data : e))
        );
        toast.success("Event updated");
      }
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (evt) => {
    if (!window.confirm(`Delete "${evt.title}"? This cannot be undone.`)) return;
    try {
      await authedClient().delete(`/admin/events/${evt.id}`);
      setEvents((prev) => prev.filter((e) => e.id !== evt.id));
      toast.success("Event deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleFeatured = async (evt) => {
    try {
      const { data } = await authedClient().patch(`/admin/events/${evt.id}`, {
        featured: !evt.featured,
      });
      setEvents((prev) => prev.map((e) => (e.id === evt.id ? data : e)));
    } catch {
      toast.error("Could not update featured");
    }
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">★ ADMIN</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Events</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            Create, edit, and feature events shown across the site.
          </p>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn-primary" data-testid="admin-events-new-button">
            <Plus className="w-4 h-4" /> New event
          </button>
        )}
      </div>

      {editing && (
        <form
          onSubmit={save}
          className="bg-[color:var(--cream)] rounded-2xl p-6 mb-8 space-y-4"
          data-testid="admin-event-form"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-[color:var(--blue-ink)]">
              {editing === "new" ? "New event" : "Edit event"}
            </h2>
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="admin-event-form-cancel"
              className="text-[color:var(--ink-soft)] hover:text-[color:var(--blue-deep)]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <Field label="Title *">
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="event-form-title"
              className={inputClass}
            />
          </Field>

          <Field label="Description *">
            <textarea
              required
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="event-form-description"
              className={inputClass}
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Start date *">
              <input
                required
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="event-form-start-date"
                className={inputClass}
              />
            </Field>
            <Field label="Start time">
              <input
                value={form.start_time || ""}
                placeholder="7:00 PM"
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid="event-form-start-time"
                className={inputClass}
              />
            </Field>
            <Field label="End time">
              <input
                value={form.end_time || ""}
                placeholder="9:00 PM"
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className={inputClass}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Location">
              <input
                value={form.location || ""}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="event-form-category"
                className={inputClass}
              >
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Flyer image">
            <div className="flex items-start gap-3">
              {form.flyer_url ? (
                <img
                  src={
                    form.flyer_url.startsWith("/api/")
                      ? `${process.env.REACT_APP_BACKEND_URL}${form.flyer_url}`
                      : form.flyer_url
                  }
                  alt="Flyer"
                  className="w-24 h-24 object-cover rounded-lg border border-[color:var(--line)]"
                />
              ) : (
                <div className="w-24 h-24 rounded-lg border border-dashed border-[color:var(--line)] flex items-center justify-center text-[color:var(--muted)]">
                  <ImageIcon className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input
                  type="url"
                  value={form.flyer_url || ""}
                  placeholder="https:// or pick from library"
                  onChange={(e) => setForm({ ...form, flyer_url: e.target.value })}
                  data-testid="event-form-flyer-url"
                  className={inputClass}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    data-testid="event-form-pick-flyer"
                    className="btn-ghost text-sm py-2 px-3"
                  >
                    Pick from library
                  </button>
                  {form.flyer_url && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, flyer_url: "" })}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="RSVP URL">
              <input
                type="url"
                value={form.rsvp_url || ""}
                onChange={(e) => setForm({ ...form, rsvp_url: e.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Payment URL">
              <input
                type="url"
                value={form.payment_url || ""}
                onChange={(e) => setForm({ ...form, payment_url: e.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Details URL">
              <input
                type="url"
                value={form.details_url || ""}
                onChange={(e) => setForm({ ...form, details_url: e.target.value })}
                className={inputClass}
              />
            </Field>
          </div>

          <div className="flex flex-wrap gap-5 pt-2">
            <Checkbox
              label="Featured (show on homepage)"
              checked={form.featured}
              onChange={(v) => setForm({ ...form, featured: v })}
              testid="event-form-featured"
            />
            <Checkbox
              label="Recurring"
              checked={form.recurring}
              onChange={(v) => setForm({ ...form, recurring: v })}
            />
            <Checkbox
              label="RSVP required"
              checked={form.rsvp_required}
              onChange={(v) => setForm({ ...form, rsvp_required: v })}
            />
          </div>

          <div className="flex flex-wrap gap-3 pt-2 items-center">
            <button type="submit" className="btn-primary" data-testid="event-form-save">
              <Save className="w-4 h-4" /> Save event
            </button>
            <button type="button" onClick={cancelEdit} className="btn-ghost">
              Cancel
            </button>
            {editing !== "new" && (
              <button
                type="button"
                onClick={() => remove({ id: editing, title: form.title })}
                data-testid="event-form-delete"
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-red-600 border border-red-200 hover:bg-red-50 text-sm font-medium"
                title="Delete this event permanently"
              >
                <Trash2 className="w-4 h-4" /> Delete event
              </button>
            )}
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        {loading ? (
          <p className="text-center text-[color:var(--muted)] py-8">Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-center text-[color:var(--muted)] py-8">
            No events yet. Click &quot;New event&quot; to add one.
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="admin-events-table">
            <thead className="text-left text-[color:var(--muted)] uppercase text-xs tracking-wider">
              <tr>
                <th className="py-3 pr-4">Title</th>
                <th className="py-3 pr-4">Date</th>
                <th className="py-3 pr-4">Category</th>
                <th className="py-3 pr-4">Last edited</th>
                <th className="py-3 pr-4">Featured</th>
                <th className="py-3 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((evt) => (
                <tr
                  key={evt.id}
                  className="border-t border-[color:var(--line)]"
                  data-testid={`admin-event-row-${evt.id}`}
                >
                  <td className="py-3 pr-4 font-medium text-[color:var(--blue-ink)]">
                    {evt.title}
                  </td>
                  <td className="py-3 pr-4 text-[color:var(--ink-soft)]">
                    {evt.start_date} {evt.start_time && `· ${evt.start_time}`}
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-xs px-2 py-1 rounded-full bg-[color:var(--cream)] text-[color:var(--blue-deep)]">
                      {evt.category}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-xs text-[color:var(--ink-soft)]" data-testid={`event-lastedit-${evt.id}`}>
                    {evt.last_edited_by ? (
                      <>
                        <div className="truncate max-w-[160px]" title={evt.last_edited_by}>{evt.last_edited_by}</div>
                        {evt.last_edited_at && (
                          <div className="text-[color:var(--muted)]">
                            {new Date(evt.last_edited_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-[color:var(--muted)]">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <button
                      onClick={() => toggleFeatured(evt)}
                      data-testid={`event-toggle-featured-${evt.id}`}
                      className={`p-2 rounded-full transition ${
                        evt.featured
                          ? "bg-[color:var(--honey)] text-[color:var(--blue-ink)]"
                          : "bg-[color:var(--cream)] text-[color:var(--muted)] hover:bg-[color:var(--cream-soft)]"
                      }`}
                      title={evt.featured ? "Featured" : "Click to feature"}
                    >
                      <Star
                        className="w-4 h-4"
                        fill={evt.featured ? "currentColor" : "none"}
                      />
                    </button>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => startEdit(evt)}
                        data-testid={`event-edit-${evt.id}`}
                        title="Edit this event"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[color:var(--blue-deep)] hover:bg-[color:var(--cream)] text-sm font-medium"
                      >
                        <Pencil className="w-4 h-4" /> Edit
                      </button>
                      <button
                        onClick={() => remove(evt)}
                        data-testid={`event-delete-${evt.id}`}
                        title="Delete this event permanently"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 text-sm font-medium"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(asset) => setForm((prev) => ({ ...prev, flyer_url: asset.public_url }))}
      />
    </div>
  );
}

const inputClass =
  "w-full rounded-lg border border-[color:var(--line)] px-3 py-2 bg-white focus:outline-none focus:border-[color:var(--blue)]";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-[color:var(--ink-soft)]">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="w-4 h-4 accent-[color:var(--blue)]"
      />
      {label}
    </label>
  );
}
